import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrg } from '../../hooks';

// Matches a specific lead's pages (/leads/:leadId and its nested routes),
// but not the leads list (/leads) or the create page (/leads/create).
const LEAD_DETAILS_PATH = /^\/leads\/(?!create(?:\/|$))[^/]+/;

const OrgSwitcher = () => {
  const { organizations, activeOrg, switchOrg, switching, isSuperAdmin } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!activeOrg) return null;
  if (!isSuperAdmin && organizations.length <= 1) return null;
  // Hide the org switch on a specific lead's pages — the lead belongs to the
  // active org, and switching (which reloads) would leave a lead not in scope.
  if (LEAD_DETAILS_PATH.test(pathname)) return null;

  const handleSwitch = async (orgId) => {
    if (orgId === activeOrg.id || switching) return;
    setOpen(false);
    await switchOrg(orgId);
    window.location.reload();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E0E0E0] dark:border-[#3A3C3E] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] transition-colors"
      >
        {activeOrg.logo_url ? (
          <img src={activeOrg.logo_url} alt="" className="w-6 h-6 rounded object-contain" />
        ) : (
          <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
            {activeOrg.name.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-sm font-medium text-[#373D3F] dark:text-[#E1E1E1] max-w-[120px] truncate">
          {activeOrg.name}
        </span>
        <svg
          className={`w-4 h-4 text-[#90A4AE] dark:text-[#6E7278] transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-[#242628] rounded-lg shadow-lg border border-[#E0E0E0] dark:border-[#3A3C3E] py-1 z-50 animate-dropdown-in origin-top-left">
          <div className="px-3 py-2 text-xs font-medium text-[#90A4AE] dark:text-[#6E7278] border-b border-[#E0E0E0] dark:border-[#3A3C3E]">
            Switch Organization
          </div>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSwitch(org.id)}
              disabled={switching}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] transition-colors disabled:opacity-50 ${
                org.id === activeOrg.id ? 'bg-primary/5' : ''
              }`}
            >
              {org.logo_url ? (
                <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-contain" />
              ) : (
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">
                  {org.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#373D3F] dark:text-[#E1E1E1] truncate">{org.name}</p>
                <p className="text-xs text-[#90A4AE] dark:text-[#6E7278] capitalize">{(org.role || '').replace('_', ' ')}</p>
              </div>
              {org.id === activeOrg.id && (
                <svg className="w-4 h-4 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
